import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Pill,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  formatInstant,
} from '@/features/inventory/registerUi';
import { inventoryErrorKey } from '@/features/inventory/inventoryErrors';
import {
  downloadInstallationsExport,
  useInstallationsList,
  type InstallationListFilters,
} from '@/features/installations/useInstallations';
import { useTenants } from '@/features/tenants/useTenants';

/**
 * THE INSTALL REGISTER (slice 30) — canvas `3d`'s left table, scoped to the
 * install RECORD (Mahad, 14 Sep: no stock, no job scheduling). All staff: the
 * list route is `requireStaff` and carries no IMEI, so a support hire reads it.
 *
 * 🔴 THE TRACKER IS NAMED BY MODEL, NEVER BY IMEI. The list wire deliberately
 * carries none; the IMEI lives behind the super-admin `/devices` guard.
 *
 * Filters (customer · installer · date range) are committed on Apply rather than
 * per keystroke, so typing an installer name does not fire a query on every
 * letter. Export CSV runs the same committed filters and refuses a truncated
 * file (the shared `X-Report-Row-Count` contract).
 */

const START_OF_DAY = 'T00:00:00.000';
const END_OF_DAY = 'T23:59:59.999';

/** A local date (`YYYY-MM-DD`) → an ISO instant with offset, or undefined. */
function dayToIso(day: string, edge: string): string | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}${edge}`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

interface Draft {
  tenantId: string;
  installer: string;
  from: string;
  to: string;
}

const EMPTY_DRAFT: Draft = { tenantId: '', installer: '', from: '', to: '' };

export function Installations() {
  const { t, i18n } = useTranslation();
  const tenantsQ = useTenants();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<Draft>(EMPTY_DRAFT);
  const [exportErrorKey, setExportErrorKey] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const filters: InstallationListFilters = useMemo(
    () => ({
      tenantId: applied.tenantId || undefined,
      installer: applied.installer.trim() || undefined,
      from: dayToIso(applied.from, START_OF_DAY),
      to: dayToIso(applied.to, END_OF_DAY),
    }),
    [applied],
  );

  const q = useInstallationsList(filters);
  const tenants = Array.isArray(tenantsQ.data?.tenants) ? tenantsQ.data.tenants : [];
  const rows = Array.isArray(q.data?.installations) ? q.data.installations : null;

  const setD = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [k]: v }));

  async function runExport() {
    setExportErrorKey(null);
    setExporting(true);
    try {
      await downloadInstallationsExport(filters);
    } catch (e) {
      setExportErrorKey(inventoryErrorKey(e));
    } finally {
      setExporting(false);
    }
  }

  return (
    <section aria-labelledby="installations-heading">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 id="installations-heading" className="font-display text-xl font-bold tracking-tight">
          {t('installations.title')}
        </h1>
        <button
          type="button"
          className={`${PRIMARY_BUTTON} order-last ms-auto`}
          onClick={() => void runExport()}
          disabled={exporting || rows === null || rows.length === 0}
        >
          {exporting ? t('installations.exporting') : t('installations.exportCsv')}
        </button>
        {rows !== null ? (
          <p className="text-sm text-raad-muted">
            {t('installations.listCount', { count: rows.length })}
          </p>
        ) : null}
      </div>

      <form
        className="mt-4 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(draft);
        }}
      >
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-raad-muted">
            {t('installations.filter.customer')}
          </span>
          <select
            value={draft.tenantId}
            onChange={(e) => setD('tenantId', e.target.value)}
            className="min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          >
            <option value="">{t('installations.filter.allCustomers')}</option>
            {tenants.map((tn) => (
              <option key={tn.id} value={tn.id}>
                {tn.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-raad-muted">
            {t('installations.filter.installer')}
          </span>
          <input
            value={draft.installer}
            onChange={(e) => setD('installer', e.target.value)}
            placeholder={t('installations.filter.installerPlaceholder')}
            className="min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-raad-muted">
            {t('installations.filter.from')}
          </span>
          <input
            type="date"
            value={draft.from}
            onChange={(e) => setD('from', e.target.value)}
            className="min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-raad-muted">
            {t('installations.filter.to')}
          </span>
          <input
            type="date"
            value={draft.to}
            onChange={(e) => setD('to', e.target.value)}
            className="min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          />
        </label>
        <button type="submit" className={SECONDARY_BUTTON}>
          {t('installations.filter.apply')}
        </button>
      </form>

      {exportErrorKey !== null ? (
        <p
          role="alert"
          className="mt-3 rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px]"
        >
          {t(exportErrorKey)}
        </p>
      ) : null}

      {q.isPending ? (
        <p className="mt-4 text-sm text-raad-muted">{t('installations.loading')}</p>
      ) : q.isError || rows === null ? (
        <p role="alert" className="mt-4 text-sm text-raad-error">
          {t('installations.error')}
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('installations.empty')}</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('installations.col.vehicle')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('installations.col.customer')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('installations.col.installer')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('installations.col.tracker')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('installations.col.installed')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('installations.col.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-raad-border last:border-b-0">
                  <td className="py-3 pr-4 align-top">
                    <span className="num font-medium">{r.vehiclePlate}</span>
                  </td>
                  <td className="py-3 pr-4 align-top">{r.tenantName}</td>
                  <td className="py-3 pr-4 align-top">
                    <span>{r.installerName}</span>
                    {r.installerPhone ? (
                      <span className="mt-0.5 block text-[11.5px] text-raad-muted num">
                        {r.installerPhone}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-3 pr-4 align-top">
                    {r.trackerModel ?? (
                      <span className="text-raad-muted" aria-hidden>
                        &mdash;
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4 align-top num text-[12px]">
                    {formatInstant(r.installedAt, i18n.language)}
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <Pill
                      label={t(`installations.status.${r.status}`)}
                      tone={
                        r.status === 'confirmed'
                          ? 'border-raad-primary/50 text-raad-accent'
                          : 'border-raad-warn/40 text-raad-warn'
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default Installations;
